import { useCallback, useEffect, useState } from 'react';
import type { StreamChat } from 'stream-chat';

import { mapChannel } from '../data/stream.js';
import type { InboxItem } from '../fixtures/domain.js';
import { needsReply } from '../fixtures/rules.js';
import { cidParts } from '../lib/bridge.js';

/** Stream caps queryChannels at 30 per page, so a larger list is queried in chunks. */
const STREAM_QUERY_CAP = 30;

export interface ListMembers {
  /** The list's members, resolved from Stream (open OR ended), most-recent first. */
  items: InboxItem[];
  loading: boolean;
  /** A resolve failed. The members already on screen are kept — only a first load
   *  with nothing to show should surface an error/retry. */
  error: boolean;
  /** Re-run the resolve: retry after an error, or refresh after a stage/End that
   *  changed a member's phase without changing the stored ids. */
  reload: () => void;
}

type MemberState = Omit<ListMembers, 'reload'>;

const NO_MEMBERS: InboxItem[] = [];

/** `prev` when nothing moved, so a revision bump costs no second render pass. */
function nextState(
  prev: MemberState,
  items: InboxItem[],
  loading: boolean,
  error: boolean,
): MemberState {
  return prev.items === items && prev.loading === loading && prev.error === error
    ? prev
    : { items, loading, error };
}

/**
 * A saved list's actual members, resolved against Stream by id.
 *
 * WHY THIS EXISTS (so a future reader can retrace the fix):
 * The list detail used to render `loadedOpenInboxPage ∩ storedIds` — it filtered
 * the first page of the general open inbox down to the list. So a member below that
 * page (or one whose stored id didn't match a loaded row) never appeared as a row,
 * which meant it could not be selected, and therefore could not be REMOVED from the
 * list, and the count read "0 of N". This resolves the list's stored ids directly
 * and returns the mapped members to render, so every member shows and is removable
 * regardless of what happens to be on the inbox page. It is also the single source
 * for the detail's count (the caller derives it from `items`). A stored id that
 * no longer resolves is simply dropped from `items` — it can't render as a row,
 * and stays counted in the total via the list's channelIds, matching the mock.
 *
 * Cheap-ish: watch:false so it opens no live subscriptions (a list detail is a
 * snapshot, not the live inbox); state:true so each channel carries the members and
 * last message a row needs.
 */
export function useListMembers(
  client: StreamChat | null,
  channelIds: string[],
  /**
   * Bump to re-resolve without the ids changing. `watch: false` means these
   * channels report nothing on their own, so anything the rows read off live
   * Stream state — `unreadCount`, which the Unread pill now cuts on — is as
   * stale as the last resolve until a caller says otherwise.
   */
  revision = 0,
): ListMembers {
  // Key on the ids so the effect re-runs when the list's membership changes.
  const key = channelIds.join(',');
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((n) => n + 1), []);
  const [state, setState] = useState<MemberState>({
    items: NO_MEMBERS,
    loading: true,
    error: false,
  });

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setState((prev) => nextState(prev, NO_MEMBERS, false, false));
      return;
    }
    if (!client) {
      // Can't resolve until connected; hold whatever is shown, skeleton if nothing.
      setState((prev) => nextState(prev, prev.items, prev.items.length === 0, false));
      return;
    }

    let cancelled = false;
    // Skeleton only on the first load. A re-query (membership changed, or reload)
    // keeps the current rows on screen and swaps them when it resolves, rather than
    // flashing the whole list back to a spinner. Clear any prior error while trying.
    setState((prev) => nextState(prev, prev.items, prev.items.length === 0, false));

    (async () => {
      const userId = client.userID ?? '';
      // Stream's `id` filter takes the BARE channel id, but a saved list stores the
      // cid (Channel.id = c.cid) — strip it, same contract as hydrateChannels.
      const bareIds = ids.map((cid) => cidParts(cid).channelId);
      const items: InboxItem[] = [];
      // Real last-activity ms per member, for ordering. mapChannel stamps
      // lastActivityAt to NOW when a channel has no messages (a started-but-unsent
      // conversation), which would sort that empty row above every active one and
      // re-stamp it to "now" on each re-query. Order by the true last_message_at
      // instead, treating "none" as epoch so an empty member sorts last, not newest.
      const activityAt = new Map<string, number>();
      for (let i = 0; i < bareIds.length; i += STREAM_QUERY_CAP) {
        const batch = bareIds.slice(i, i + STREAM_QUERY_CAP);
        const channels = await client.queryChannels({ id: { $in: batch } }, [], {
          watch: false,
          state: true,
          limit: batch.length,
        });
        for (const channel of channels) {
          const mapped = mapChannel(channel, userId);
          items.push({
            channel: mapped.channel,
            counterpart: mapped.counterpart,
            needsReply: needsReply(mapped.channel),
          });
          // Mirror mapChannel's own derivation (data/stream.ts): last_message_at,
          // else the last message's time — so a channel whose history came back but
          // whose last_message_at is null still sorts by its real activity, and the
          // sort key can't disagree with the time the row prints.
          const at = channel.state?.last_message_at ?? channel.state?.messages?.at(-1)?.created_at;
          activityAt.set(mapped.channel.id, at ? new Date(at).getTime() : 0);
        }
      }
      // Stream's `$in` ignores sort and returns per-chunk, so restore recency order
      // (most recently active first), the way the inbox lists conversations.
      items.sort(
        (a, b) => (activityAt.get(b.channel.id) ?? 0) - (activityAt.get(a.channel.id) ?? 0),
      );
      if (!cancelled) setState({ items, loading: false, error: false });
    })().catch(() => {
      // Keep whatever is on screen (a re-query blip must not blank the list); flag
      // the error so a FIRST load — nothing resolved yet — can show a retry.
      if (!cancelled) setState((prev) => nextState(prev, prev.items, false, true));
    });

    return () => {
      cancelled = true;
    };
  }, [client, key, reloadKey, revision]);

  return { ...state, reload };
}
